'use client';

import { useState } from 'react';
import type { Page, ContactPage } from '../../lib/types';
import ContentRenderer from '../ui/ContentRenderer';
import logger from '../../lib/logger';
import { getApiBaseUrl } from '../../lib/utils';
import Cookies from '../sections/Cookies';

type ContactLayoutProps = {
  page: Page;
};

export default function ContactLayout({ page }: ContactLayoutProps) {
  const contactPage = page as ContactPage;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name') as string,
      company: formData.get('company') as string,
      email: formData.get('email') as string,
      message: formData.get('message') as string,
    };

    const API_BASE_URL = getApiBaseUrl();
    const API_KEY = process.env.NEXT_PUBLIC_API_KEY;
    
    if (!API_BASE_URL || !API_KEY) {
      logger.error('Missing API configuration. Please set NEXT_PUBLIC_API_URL and NEXT_PUBLIC_API_KEY environment variables.');
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      setSubmitStatus('success');
      
      // Reset form
      e.currentTarget.reset();
      
    } catch (error) {
      logger.error('Contact API Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="text-center mb-16">
        <h1 className="text-5xl font-bold mb-6">{page.title}</h1>
        {page.subtitle && (
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {page.subtitle}
          </p>
        )}
      </div>

      {/* Main Content */}
      {page.body && (
        <div className="max-w-4xl mx-auto mb-16 text-center">
          <ContentRenderer content={page.body} />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Contact Form */}
        <div>
          <h2 className="text-2xl font-bold mb-6">Send us a message</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6" aria-describedby="contact-form-description">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                aria-required="true"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-transparent"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                aria-required="true"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-transparent"
              />
            </div>
            
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                Company *
              </label>
              <input
                type="text"
                id="company"
                name="company"
                aria-required="true"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-transparent"
              />
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                placeholder="Tell us about your project, goals, timeline, and any specific requirements..."
                className="w-full px-4 py-3 border border-gray-300 rounded-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-transparent resize-vertical"
              ></textarea>
            </div>
            
            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded" role="status" aria-live="polite">
                Thank you for your message! We&rsquo;ll get back to you soon.
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded" role="alert" aria-live="assertive">
                Sorry, there was an error sending your message. Please try again.
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-black text-white font-semibold px-8 py-4 rounded-none hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>

        {/* Contact Information */}
        <div>
          <h2 className="text-2xl font-bold mb-6">Get in touch</h2>
          
          {contactPage.contactInfo ? (
            <div className="space-y-6 mb-8">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Office</h3>
                <p className="text-gray-600 whitespace-pre-line">
                  {contactPage.contactInfo.address}
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                <a 
                  href={`tel:${contactPage.contactInfo.phone}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {contactPage.contactInfo.phone}
                </a>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                <a 
                  href={`mailto:${contactPage.contactInfo.email}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {contactPage.contactInfo.email}
                </a>
              </div>
              
              {contactPage.contactInfo.hours && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Hours</h3>
                  <p className="text-gray-600">{contactPage.contactInfo.hours}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6 mb-8">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                <a 
                  href="mailto:hello@havascx-us.com"
                  className="text-blue-600 hover:text-blue-800"
                >
                  hello@havascx-us.com
                </a>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                <a 
                  href="tel:+1234567890"
                  className="text-blue-600 hover:text-blue-800"
                >
                  +1 (234) 567-890
                </a>
              </div>
            </div>
          )}

          {/* Map */}
          {contactPage.contactInfo?.mapUrl && (
            <div className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
              <iframe
                src={contactPage.contactInfo.mapUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              ></iframe>
            </div>
          )}

          {/* FAQ or Additional Info */}
          <div className="mt-8 p-6 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-4">Quick Response Times</h3>
            <div className="space-y-3 text-sm text-gray-600">
              <p>📧 Email responses within 24 hours</p>
              <p>📞 Phone consultations available</p>
              <p>🚀 Free project estimates</p>
              <p>💼 NDA available upon request</p>
            </div>
          </div>
        </div>
      </div>
      {/* Cookies */}
      <Cookies />
    </div>
  );
}

